'use client';

import React from 'react';
import { icons } from './icons.tsx';
import classnames from 'classnames';

import Row from '../Grid/Row/Row.tsx';

import styles from './Iconography.module.scss';

type IconSize = 8 | 12 | 16 | 20 | 24 | 32 | 64;
type Theme = 'dark' | 'light';
type IconCategory =
  | 'global'
  | 'arrows'
  | 'hardware'
  | 'media'
  | 'map'
  | 'features'
  | 'social'
  | 'brand'
  | 'privacy';

type IconColor =
  | 'primary-1'
  | 'primary-2'
  | 'white'
  | 'gray-1'
  | 'gray-5'
  | 'gray-7'
  | 'gray-9'
  | 'black'
  | 'success-1'
  | 'warning-1'
  | 'error-1'
  | 'information-1'
  | 'link';

interface ResponsiveSize {
  xs?: IconSize;
  sm?: IconSize;
  md?: IconSize;
  lg?: IconSize;
  xl?: IconSize;
  xxl?: IconSize;
}

export interface IconographyProps extends React.HTMLAttributes<HTMLDivElement> {
  className?: string;
  category: IconCategory;
  color?: IconColor;
  name: string;
  handleClick?: () => void;
  theme?: Theme;
  size?: IconSize | 'auto' | ResponsiveSize;
  dataTestId?: string;
}

const Iconography: React.FC<IconographyProps> = ({
  className,
  category,
  color = 'black',
  name,
  handleClick,
  theme = 'dark',
  size = { xs: 16, md: 20, lg: 24 },
  dataTestId = 'icon',
  ...props
}) => {
  // Check if icon exists first, before any other logic
  if (!icons?.[category]?.[name]) return null;

  const classList: (string | undefined)[] = [className];

  const defaultSize = typeof size === 'object' ? null : { height: `${size}px`, width: `${size}px` };

  const updateSizing = (value: IconSize | 'auto' | ResponsiveSize) => {
    if (typeof value === 'number' || value === 'auto') {
      classList.push(styles[`${value}`]);
    } else {
      for (const sizeKey in value) {
        const breakpoint = sizeKey as keyof ResponsiveSize;
        const sizeValue = value[breakpoint];
        if (sizeValue !== undefined) {
          classList.push(styles[`${sizeKey}-${sizeValue}`]);
        }
      }
    }
  };

  updateSizing(size);

  return (
    <Row
      className={classnames(classList)}
      gutter={[0, 0]}
      align="middle"
      onClick={handleClick}
      {...props}
    >
      {icons?.[category]?.[name](
        styles[`${theme}-${color}`],
        defaultSize && (typeof size === 'number' || size === 'auto') ? size : '',
        dataTestId
      )}
    </Row>
  );
};

Iconography.displayName = 'Iconography';

export default Iconography;
